import React, { useEffect, useRef, useState } from "react";
import checkIcon from "../../../assets/check.png";
import { RequirementContainer } from "hooks/useRequirement";
import { Modal, Button } from "@cpnw/ui";
import classNames from "classnames";
import { formatToLocalDateFrom } from "utils/helpers";
import styles from "./NotificationsListDash.module.css";

export const NotificationsListDash = ({ notifications = [], onViewAll }) => {
    const {
        refetchContactNotices,
        putContactNotices
    } = RequirementContainer.useContainer();
    const DEFAULT_ITEMS_SHOWN = 5;
    const [showAll, setShowAll] = useState(false);
    const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);
    const [notificationSelected, setNotificationSelected] = useState(null);
    const [notificationContent, setNotificationContent] = useState("");
    const scrollRef = useRef(null);
    const statusPalettes = [
        { label: "New", color: "#5ba9d4", background: "rgba(224, 244, 255, 2)" },              // Indigo 700
        { label: "DM", color: "#a59d78", background: "#f5f0da" },
        { label: "Accepted", color: "#74c000", background: "rgba(234, 255, 211, 2)" },          // Emerald 600
        { label: "Approved", color: "#0EA5E9", background: "rgba(14, 165, 233, 0.08)" },         // Sky 500
        { label: "Rejected", color: "#cc2f0b", background: "rgba(255, 232, 227, 2)" },          // Red 600
        { label: "Conditionally Approved", color: "#D97706", background: "rgba(217, 119, 6, 0.08)" }, // Amber 600
        { label: "Expiring", color: "#FFCE54", background: "rgba(248, 166, 7, 0.1)" },         // Yellow 500
        { label: "Expired", color: "#333333", background: "rgba(0, 0, 0, 0.1)" },          // Violet 600
        { label: "Dismiss", color: "#656D78", background: "rgba(204,209,217, 0.1)" }          // Gray 400
    ];

    // const [notifications, setNotifications] = useState([
    //     // ✅ Today
    //     { date: "06.20.2025", name: "Unread: Today", status: "New", IsRead: false, isDismiss: false },
    //     { date: "06.20.2025", name: "Read: Today", status: "New", IsRead: false, isDismiss: false },
    //     { date: "06.20.2025", name: "Click to dismiss", status: "Dismiss", IsRead: false, isDismiss: false },

    //     // ✅ This week
    //     { date: "06.19.2025", name: "Dismissed: Read this week", status: "Accepted", IsRead: true, isDismiss: false },
    //     { date: "06.18.2025", name: "Unread: Recent", status: "Rejected", IsRead: true, isDismiss: false },
    //     { date: "06.17.2025", name: "Read: This week", status: "DM", IsRead: true, isDismiss: false },

    //     // ✅/❌ This week, dismissed
    //     { date: "06.15.2025", name: "Dismissed: Unread recent", status: "New", IsRead: true, isDismiss: false },

    //     // ✅ Older than a week
    //     { date: "06.10.2025", name: "Unread: Over a week ago", status: "New", IsRead: false, isDismiss: false },
    //     { date: "06.09.2025", name: "Read: Over a week ago", status: "Accepted", IsRead: false, isDismiss: false },
    //     { date: "06.05.2025", name: "Dismissed: Old read", status: "DM", IsRead: true, isDismiss: false }
    // ]);

    function getStartOfWeek(date) {
        const day = date.getDay(); // 0 (Sun) to 6 (Sat)
        const diff = (day === 0 ? -6 : 1) - day; // Monday is 1
        const monday = new Date(date);
        monday.setDate(monday.getDate() + diff);
        monday.setHours(0, 0, 0, 0);
        return monday;
    }

    function getEndOfWeek(date) {
        const monday = getStartOfWeek(date);
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);
        return sunday;
    }

    function parseDate(str) {
        if (!str || typeof str !== "string") return new Date("Invalid Date");

        const [month, day, year] = str.split(".");
        if (!month || !day || !year) return new Date("Invalid Date");

        return new Date(`${year}-${month}-${day}`);
    }

    const now = new Date();
    const startOfWeek = getStartOfWeek(now);
    const endOfWeek = getEndOfWeek(now);

    // Filter out dismissed notifications first
    // const visibleNotifications = notifications.filter(n => !n.isDismiss);

    // Filter: unread always keep, read only if in current week
    const unread = notifications.filter((n) => !n.IsRead);
    const read = notifications.filter((n) => n.IsRead && parseDate(n.Date) >= startOfWeek && parseDate(n.Date) <= endOfWeek);

    // Sort both by date descending
    const sortByDateDesc = (a, b) => parseDate(b.Date) - parseDate(a.Date);
    unread.sort(sortByDateDesc);
    read.sort(sortByDateDesc);

    // Take up to LIMIT_ITEMS_PER_PAGE unread, then fill with read to reach LIMIT_ITEMS_PER_PAGE
    const selectedUnread = unread.slice(0, DEFAULT_ITEMS_SHOWN);
    const remainingCount = DEFAULT_ITEMS_SHOWN - selectedUnread.length;
    const selectedRead = read.slice(0, remainingCount);

    const limitedNotifications = [...selectedUnread, ...selectedRead];

    // Decide what to display
    const displayList = showAll ? notifications.sort(sortByDateDesc) : limitedNotifications.sort(sortByDateDesc);

    // Handle Dismiss click:
    const handleDismissClick = (message) => {
        const payload = { id: message.Id, isDismiss: true, isRead: true }
        putContactNotices({ payload }).then((result) => {
            if (result.success) {
                refetchContactNotices();
            }
        });
    };

    // Handle Dismiss click:
    const handleReadMessage = (message) => {
        setNotificationContent(message.Content)
        setNotificationModalOpen(true)
    };

    // Handle Dismiss click:
    const handleCloseNotification = (message) => {
        setNotificationContent("")
        setNotificationModalOpen(false)
        const payload = { id: message.Id, isRead: true }
        !message.IsRead && putContactNotices({ payload }).then((result) => {
            if (result.success) {
                refetchContactNotices();
            }
        });
    };



    useEffect(() => {
        if (showAll && scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [showAll]);

    return (
        <div className={styles.notificationsList}>
            <div className={styles.cardHeader}>
                <h2>Notifications</h2>
                <button className={styles.viewAll} onClick={() => setShowAll((prev) => !prev)}>
                    {showAll ? "Show Less" : "View All"}
                </button>
            </div>

            {displayList.length === 0 ? (
                <div className={styles.emptyMessage}>
                    <img src={checkIcon} className={styles.emptyImage} />
                    <div className={styles.emptyText}>You're all caught up!</div>
                </div>
            ) : (
                <div className={(showAll && notifications.length > DEFAULT_ITEMS_SHOWN) ? styles.scrollContainer : styles.noScrollcontainer} ref={scrollRef} style={{ maxHeight: (DEFAULT_ITEMS_SHOWN * 60) + "px" }}>
                    {displayList.map((item, index) => {
                        const statusInfo = statusPalettes.find((p) => p.label === item.Status);
                        const color = statusInfo?.color || "#000";
                        const background = statusInfo?.background || "transparent";

                        return (
                            <div key={index} className={styles.container} onClick={() => {
                                setNotificationSelected(item);
                                handleReadMessage(item);
                            }}>
                                <div className={styles.icon}>
                                    {item.IsRead ? (
                                        <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="7" cy="7" r="6" stroke="#CECECE" strokeWidth="1" fill="none" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="7" cy="7" r="6" stroke="transparent" strokeWidth="1" fill="#007e8d" />
                                        </svg>
                                    )}
                                </div>

                                <div className={styles.date}>{formatToLocalDateFrom(item.NoticeDate)}</div>

                                <div className={styles.name} style={{ fontWeight: item.IsRead ? "normal" : 600 }} title={item.Content}>
                                    {item.Content}
                                </div>

                                <div className={styles.status} style={{ color, background }} onClick={() => {
                                    setNotificationSelected(item);
                                    if (item.Status === "Dismiss") {
                                        handleDismissClick(item);
                                    } else {
                                        handleReadMessage(item);
                                    }
                                }}>
                                    {item.Status === "Dismiss" ? (
                                        <div style={{ fontWeight: item.IsRead ? "normal" : 600 }}>
                                            {item.Status} <span style={{ marginLeft: "4px" }}>✕</span>
                                        </div>
                                    ) : (
                                        <span style={{ fontWeight: item.IsRead ? "normal" : 600 }}>{item.Status}</span>
                                    )}
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}
            {isNotificationModalOpen ? <Modal
                isOpen={isNotificationModalOpen}
                onRequestClose={() => {
                    handleCloseNotification(notificationSelected)
                }}
                className={classNames([styles.notificationModal])}
            >
                <h3>Notification</h3>
                <ul className={styles.notificationContent}>
                    {formatToLocalDateFrom(notificationSelected.NoticeDate)}
                    <br />
                    {notificationSelected.Status == "DM"
                        ? `(From Requirement: ${notificationSelected.RequirementName}) ${notificationSelected.Content}`
                        : notificationSelected.Content
                    }
                </ul>

                <div className={styles.closeButton}>
                    <Button
                        text={"CLOSE"}
                        type={"submit"}
                        className={styles.customButton}
                        onClick={(ev) => {
                            handleCloseNotification(notificationSelected)
                        }}
                    />
                </div>
            </Modal> : undefined}
        </div>
    );
};

export default NotificationsListDash;
